import {
  ImExEntryDtoInput,
  ImExEntryResponseOptionInput,
  ImExEntryType,
  ImExMatrixRowInput
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  EntryType
} from '../../../data/model/questionnaire'
import { MarketVisibilityFilter } from '../../Questionnaire/MarketVisibility/marketVisibility'
import { extractTranslatableFields } from './extractTranslatableFields'
import { buildPlainStyling } from './previewOverlay'

// Builds the `entries` payload for a single-language `importQuestionnaireV2`
// call from the live draft. The backend runs in "translation mode" when a
// `languageCode` is set: it matches entries / options / rows BY POSITION
// against the existing draft and upserts the per-language text, leaving the
// master text and other languages untouched (see COR-133 / COR-137).
//
// Only Question and Matrix entries are emitted here:
//   * Text cards are excluded — the backend translation upsert rewrites the
//     text card's subtitle from the master (English) text and there is no
//     subtitle input, so importing would clobber the language's subtitle
//     (tracked in COR-138). They stay on the per-field save.
//   * Text highlighters and conjoints are excluded for the same class of
//     reason: the importer copies the highlighter's passage from the master
//     and ignores conjoint attributes.
//   * MaxDiff's instruction labels and the slider's endpoint labels sit on an
//     importable (Question) entry but have no slot in the DTO, so they come
//     back as residual.
//   * Audience is not part of `importQuestionnaireV2` at all.
//
// Safe-title rule: the backend ALWAYS overwrites an entry's title translation
// for any entry it receives, and the renderer only falls back to the master
// when the translation record is *absent* (an empty record renders blank). So
// an entry is only emitted when its resolved title is non-empty; options and
// rows are included only when non-empty (omitted ones are left untouched).

export type TranslationOverrides = ReadonlyMap<string, string>

export interface BuildTranslationImportOptions {
  // Values not yet reflected in the Apollo cache (a live edit, an
  // auto-translation, a parsed docx cell), keyed by `TranslatableField.key`.
  // Keys are the per-type `getQuestionEntryPrefix` labels (e.g. "Q1.text"),
  // the same ones `extractTranslatableFields` and the docx projection produce.
  overrides?: TranslationOverrides
  // Drops options / rows no market in scope shows, so the import payload
  // matches what the editor offered and what completion counted.
  isVisible?: MarketVisibilityFilter
}

export interface TranslationImportBuild {
  entries: ImExEntryDtoInput[]
  // Field keys written by the import payload.
  consumedKeys: Set<string>
  // Field keys that have a value to save but cannot go via import (the
  // entry-level title is not translated yet). The caller should fall back to a
  // per-field save for these.
  residualKeys: Set<string>
}

const IMPORTABLE_ENTRY_TYPES: ReadonlySet<EntryType> = new Set([
  EntryType.QuestionEntryType,
  EntryType.MatrixEntryType
])

const entryTypeToImEx = (entryType: EntryType): ImExEntryType =>
  entryType === EntryType.MatrixEntryType
    ? ImExEntryType.MatrixType
    : ImExEntryType.BasicType

export const buildTranslationImportEntries = (
  entries: ReadonlyArray<DraftQuestionnaireEntry>,
  languageCode: string,
  { overrides, isVisible }: BuildTranslationImportOptions = {}
): TranslationImportBuild => {
  const importEntries: ImExEntryDtoInput[] = []
  const consumedKeys = new Set<string>()
  const residualKeys = new Set<string>()

  const resolve = (key: string, getCurrent: (lang: string) => string): string =>
    overrides?.get(key) ?? getCurrent(languageCode)

  entries.forEach((entry) => {
    const fields = extractTranslatableFields(entry, isVisible)
    if (fields.length === 0) return

    if (!IMPORTABLE_ENTRY_TYPES.has(entry.entryType)) {
      // Not importable (text cards etc.): anything with a value is residual.
      fields.forEach((field) => {
        if (resolve(field.key, field.getCurrent).trim()) {
          residualKeys.add(field.key)
        }
      })
      return
    }

    // Position lookups from the live draft — the backend matches by position.
    const optionPositionById = new Map<string, number>()
    const rowPositionById = new Map<string, number>()
    if (entry.entryType === EntryType.QuestionEntryType) {
      const item = entry.entryItem as DraftQuestionItem
      item.responseOptions.forEach((opt) => {
        const id = opt.responseOption?.responseOptionId
        if (id) optionPositionById.set(id, opt.position)
      })
    } else {
      const item = entry.entryItem as DraftMatrixItem
      item.responseOptions.forEach((opt) => {
        const id = opt.responseOption?.responseOptionId
        if (id) optionPositionById.set(id, opt.position)
      })
      item.matrixRows.forEach((row) => {
        const id = row.question?.questionId
        if (id) rowPositionById.set(id, row.position)
      })
    }

    let titleKey: string | undefined
    let title = ''
    const options: ImExEntryResponseOptionInput[] = []
    const rows: ImExMatrixRowInput[] = []
    const childKeys: string[] = []

    fields.forEach((field) => {
      const value = resolve(field.key, field.getCurrent)
      switch (field.kind) {
        case 'questionText':
        case 'matrixTitle':
          titleKey = field.key
          title = value
          break
        case 'responseOptionValue': {
          if (!field.subId) return
          const position = optionPositionById.get(field.subId)
          if (position === undefined || !value.trim()) return
          options.push({ value, position, isNew: false })
          childKeys.push(field.key)
          break
        }
        case 'matrixRowText': {
          if (!field.subId) return
          const position = rowPositionById.get(field.subId)
          if (position === undefined || !value.trim()) return
          rows.push({ value, position, isNew: false })
          childKeys.push(field.key)
          break
        }
        default:
          // Fields on an importable entry that the import DTO has no slot for
          // — MaxDiff's most/least/attribute labels and the slider's endpoint
          // labels. Per-field save instead.
          if (value.trim()) residualKeys.add(field.key)
          break
      }
    })

    if (!title.trim()) {
      // Cannot send this entry without clobbering the title translation, so
      // any child field that had a value falls back to a per-field save.
      childKeys.forEach((key) => residualKeys.add(key))
      return
    }

    // The renderer draws the title from its Draft.js styling blob in
    // preference to the plain text, so a translation with no styling falls
    // back to the master blob — which still carries the English text. Send a
    // plain single-block blob built from the translated title so the render
    // uses the translation. We deliberately do NOT copy the master's inline
    // formatting: its style ranges are anchored to the English text's
    // character offsets and don't map onto the translated words.
    importEntries.push({
      entryType: entryTypeToImEx(entry.entryType),
      kind: entry.questionKind,
      position: entry.position,
      isNew: false,
      title,
      styling: buildPlainStyling(title),
      options,
      rows
    })
    if (titleKey) consumedKeys.add(titleKey)
    childKeys.forEach((key) => consumedKeys.add(key))
  })

  return { entries: importEntries, consumedKeys, residualKeys }
}
